import { useEffect, useId, useRef, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface DialogProps {
  children: ReactNode;
  onClose: () => void;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  overlayStyle?: CSSProperties;
  contentStyle?: CSSProperties;
  lockBodyScroll?: boolean;
  zIndex?: number;
  /**
   * When false, the dialog stays mounted (children preserve state) but the
   * overlay is hidden, body scroll is unlocked, and focus management is off.
   * Defaults to true (visible) so existing callers are unchanged.
   */
  isOpen?: boolean;
}

const dialogStack: symbol[] = [];

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(focusableSelector)).filter((el) => {
    if (el.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return el.getClientRects().length > 0;
  });
}

export default function Dialog({
  children,
  onClose,
  ariaLabel,
  ariaLabelledBy,
  closeOnBackdrop = true,
  closeOnEscape = true,
  overlayStyle,
  contentStyle,
  lockBodyScroll = true,
  zIndex = 1000,
  isOpen = true,
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const stackIdRef = useRef(Symbol('dialog'));
  const onCloseRef = useRef(onClose);
  const closeOnEscapeRef = useRef(closeOnEscape);
  const generatedLabelId = useId();
  const labelId = ariaLabelledBy ?? (ariaLabel ? undefined : generatedLabelId);
  onCloseRef.current = onClose;
  closeOnEscapeRef.current = closeOnEscape;

  useEffect(() => {
    if (!isOpen) return;
    const stackId = stackIdRef.current;
    const isTopDialog = () => dialogStack[dialogStack.length - 1] === stackId;
    dialogStack.push(stackId);
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusTimer = window.setTimeout(() => {
      const dialog = dialogRef.current;
      if (!dialog || !isTopDialog()) return;
      if (dialog.contains(document.activeElement)) return;
      const autoFocusEl = dialog.querySelector<HTMLElement>('[autofocus]');
      const target = autoFocusEl ?? getFocusableElements(dialog)[0] ?? dialog;
      target.focus({ preventScroll: true });
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog || !isTopDialog()) return;

      if (event.key === 'Escape' && closeOnEscapeRef.current) {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (!dialog.contains(active) || active === first) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        }
      } else if (active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown, true);
      const wasTopDialog = isTopDialog();
      const stackIndex = dialogStack.indexOf(stackId);
      if (stackIndex >= 0) dialogStack.splice(stackIndex, 1);
      if (wasTopDialog && previousFocus && document.contains(previousFocus)) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!lockBodyScroll || !isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [lockBodyScroll, isOpen]);

  const dialog = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay)',
        display: isOpen ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex,
        ...overlayStyle,
      }}
      aria-hidden={isOpen ? undefined : true}
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel ? undefined : labelId}
        tabIndex={-1}
        style={contentStyle}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {!ariaLabel && !ariaLabelledBy && (
          <span id={generatedLabelId} style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            Dialog
          </span>
        )}
        {children}
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}
